import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContainerBase } from 'src/components/widgets/layouts';
import Stack from 'src/components/widgets/stack/stack';
import { TextArea } from 'src/components/widgets/textArea';
import { T, backofficeUrl, useQueryParams } from 'src/shared/helpers';
import ArrowBackIcon from 'src/shared/material_icons/navigation/arrow_back.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import LogoZakeke from 'src/shared/svg/logoZakeke';
import styled from 'styled-components';
import { BasicButton, PrimaryButton } from '../../widgets/buttons';
import { TextButton } from '../../widgets/buttons/textButton';
import { Icon } from '../../widgets/icon';
import OnboardingSolutions from './onboardingSolutions';
import { useSuggestTool } from './useSuggestTool';
import { showErrorNotification } from '../../widgets/notificationToaster';
import { ONBOARDING_TOOLS, OnboardingTool, PROMPT_PRESETS, ToolSuggestion } from './toolSuggestion';

// The Zakeke palette — orange #F08035, green #98C326, fuchsia #E61780 — as soft washes on an
// off-white ground, so the page is recognisably ours without fighting the prompt for attention.
const Container = styled(ContainerBase)`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    /* Fullscreen routes have no outer gutter, so the background can own the viewport height directly
       and still grow naturally when the solutions grid is taller than the screen. */
    flex: 1;
    min-height: 100vh;
    /* Room at the top for the logo and at the bottom for the shortcut row, both out of the flow. */
    padding: 72px 20px 80px;
    box-sizing: border-box;
    background:
        radial-gradient(circle at 12% 14%, rgba(240, 128, 53, 0.1), transparent 50%),
        radial-gradient(circle at 88% 12%, rgba(152, 195, 38, 0.09), transparent 48%),
        radial-gradient(circle at 78% 86%, rgba(230, 23, 128, 0.07), transparent 50%),
        radial-gradient(circle at 18% 92%, rgba(240, 128, 53, 0.05), transparent 46%), #fcfcfb;
`;

// Top bar of the page: the mark on the left, and — only when the page was opened from inside the app —
// the way back on the right.
const TopBar = styled.div`
    position: absolute;
    top: 28px;
    left: 32px;
    right: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

const BrandLogo = styled(LogoZakeke)`
    height: 20px;
    width: auto;
`;

const Content = styled.div`
    width: 100%;
    max-width: 640px;
`;

const Title = styled.h1`
    font-size: 32px;
    font-weight: bold;
    text-align: center;
    margin: 0;
    color: ${(props) => props.theme.app.component.pageTitle.textColor};
`;

const Subtitle = styled.p`
    font-size: 15px;
    text-align: center;
    color: #888;
    margin: 0;
`;

const PromptArea = styled(TextArea)`
    min-height: 120px;
    padding: 12px 14px;
    font-size: 15px;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
`;

// The explore option sits beside the primary CTA, quieter than it (not bold).
const ExploreLink = styled(TextButton)`
    font-weight: 400;
`;

const ToolCard = styled.div`
    background: #fff;
    border: 1px solid #dbe2e6;
    border-radius: 8px;
    padding: 24px;
`;

const CardLabel = styled.p`
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #888;
    margin: 0 0 8px 0;
`;

const ToolName = styled.h2`
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 8px 0;
`;

const ToolDescription = styled.p`
    font-size: 14px;
    line-height: 1.6;
    color: #555;
    margin: 0 0 20px 0;
`;

const AlternativeRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

// The ready-made problems are a list the seller scans top to bottom and picks from, rather than a set
// of chips: one of them is usually close enough, and reading them is the whole point.
const OptionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const OptionRow = styled.button<{ $selected?: boolean }>`
    width: 100%;
    box-sizing: border-box;
    text-align: left;
    background: ${(props) => (props.$selected ? '#fff8f3' : '#fff')};
    border: 1px solid ${(props) => (props.$selected ? '#f08035' : '#dbe2e6')};
    border-radius: 8px;
    padding: 14px 16px;
    font-family: ${(props) => props.theme.app.font};
    font-size: 14px;
    line-height: 1.4;
    color: ${(props) => (props.$selected ? '#1a1a1a' : '#555')};
    cursor: pointer;

    &:hover {
        border-color: ${(props) => (props.$selected ? '#f08035' : '#b9c4cb')};
        color: #1a1a1a;
    }
`;

// The last row is not one of the problems: it is the way out of the list, so it is dashed, unfilled and
// set slightly apart from the six above it.
const CustomOptionRow = styled(OptionRow)`
    margin-top: 6px;
    background: ${(props) => (props.$selected ? '#fff8f3' : 'transparent')};
    border-style: dashed;
    font-weight: 600;
`;

// Deliberately quiet, and out of the flow at the very bottom so the list stays centred on the screen.
// It belongs to the onboarding a seller meets after signup: someone who reopened this page from the
// solution switcher is already inside a tool and has the switcher itself for jumping around.
const ShortcutFooter = styled.div`
    position: absolute;
    left: 20px;
    right: 20px;
    bottom: 28px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: baseline;
    gap: 4px 14px;
    font-size: 12px;
    color: #aaa;
`;

const Shortcut = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: ${(props) => props.theme.app.font};
    font-size: 12px;
    color: #999;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        color: #555;
    }
`;

const PLACEHOLDER = T._('E.g. I sell t-shirts and I want my customers to add their own text and logo before ordering.', 'Admin');

// 'solutions' is the manual way through: instead of being routed by the prompt, the seller reads what
// every solution does and picks one. It has its own URL because the solution switcher links straight
// to it, for a seller who is already inside a tool.
type Step = 'prompt' | 'solutions' | 'suggestion';

export const ONBOARDING_SOLUTIONS_PATH = '/onboarding/solutions';

// Marks the page as reopened from inside the app rather than met right after signup. It travels in the
// URL rather than in the router state so it survives a reload and a pasted link.
export const ONBOARDING_FROM_APP_QUERY = 'from=app';

const Onboarding = () => {
    const navigate = useNavigate();
    const suggestTool = useSuggestTool();
    const location = useLocation();
    const querystringParams = useQueryParams();
    const promptRef = useRef<HTMLTextAreaElement>(null);

    const [step, setStep] = useState<Step>(location.pathname.endsWith(ONBOARDING_SOLUTIONS_PATH) ? 'solutions' : 'prompt');

    // Set by the solution switcher: the seller was already working inside a tool, so the page offers a
    // way out and drops the shortcuts. Right after signup there is nothing to go back to.
    const isOpenedFromApp = querystringParams.get('from') === 'app';
    const [prompt, setPrompt] = useState('');
    // The seller either picks one of the ready-made problems or writes their own; 'isCustom' is which of
    // the two the box below the list belongs to.
    const [isCustom, setIsCustom] = useState(false);
    const [suggestion, setSuggestion] = useState<ToolSuggestion | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // The solutions view lives at its own URL (/onboarding/solutions) so it can be linked to directly
    // and appears in history; the prompt form and its suggestion stay on /onboarding. Moving between
    // the two navigates rather than only flipping local state, and this keeps the step in sync with the
    // URL — for a Back/forward, a pasted link, or the solution switcher landing straight here.
    useEffect(() => {
        if (location.pathname.endsWith(ONBOARDING_SOLUTIONS_PATH)) {
            setStep('solutions');
        } else {
            setStep((prev) => (prev === 'solutions' ? 'prompt' : prev));
        }
    }, [location.pathname]);

    const goToForm = () => navigate(backofficeUrl('/onboarding') + location.search);
    const goToSolutions = () => navigate(backofficeUrl('/onboarding/solutions') + location.search);

    const goToTool = (tool: OnboardingTool) => {
        window.location.href = tool.url;
    };

    const runSuggestion = async (text: string) => {
        if (!text.trim() || isLoading) return;

        setIsLoading(true);
        try {
            setSuggestion(await suggestTool(text));
            setStep('suggestion');
        } catch {
            showErrorNotification({ message: T._('Unable to suggest a solution. Please try again.', 'Admin') });
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        runSuggestion(prompt);
    };

    // Picking a problem from the list only selects it: nothing runs until Continue.
    const onOptionClick = (preset: string) => {
        setIsCustom(false);
        setPrompt(preset);
    };

    // 'None of the above' toggles the free-text box for the seller whose case is not on the list:
    // clicking it again closes the box and leaves nothing selected.
    const onNoneOfTheAboveClick = () => {
        setPrompt('');

        if (isCustom) {
            setIsCustom(false);
            return;
        }

        setIsCustom(true);
        window.setTimeout(() => promptRef.current?.focus(), 0);
    };

    return (
        <Container>
            <TopBar>
                <BrandLogo aria-label='Zakeke' />
                {step === 'solutions' ? (
                    <BasicButton type='button' $size='small' $type='icon' aria-label={T._('Back', 'Admin')} onClick={goToForm}>
                        <Icon>
                            <ArrowBackIcon />
                        </Icon>
                    </BasicButton>
                ) : (
                    isOpenedFromApp && (
                        <BasicButton
                            type='button'
                            $size='small'
                            $type='icon'
                            aria-label={T._('Close', 'Admin')}
                            onClick={() => navigate(backofficeUrl('/dashboard'), { replace: true })}
                        >
                            <Icon>
                                <CloseIcon />
                            </Icon>
                        </BasicButton>
                    )
                )}
            </TopBar>

            {step === 'solutions' ? (
                <OnboardingSolutions onGoToTool={goToTool} />
            ) : (
                <Content>
                    {step === 'prompt' && (
                        <form onSubmit={onSubmit}>
                            <Stack $gap='24px'>
                                <Stack $gap='8px'>
                                    <Title>{T._('What would you like to do with Zakeke?', 'Admin')}</Title>
                                    <Subtitle>
                                        {T._(
                                            'Zakeke has several solutions, and they do very different things. Pick what sounds most like you and we will point you to the right one.',
                                            'Admin'
                                        )}
                                    </Subtitle>
                                </Stack>

                                <OptionList>
                                    {PROMPT_PRESETS.map((preset) => (
                                        <OptionRow
                                            key={preset}
                                            type='button'
                                            $selected={!isCustom && prompt === preset}
                                            aria-pressed={!isCustom && prompt === preset}
                                            disabled={isLoading}
                                            onClick={() => onOptionClick(preset)}
                                        >
                                            {preset}
                                        </OptionRow>
                                    ))}

                                    <CustomOptionRow
                                        type='button'
                                        $selected={isCustom}
                                        aria-pressed={isCustom}
                                        disabled={isLoading}
                                        onClick={onNoneOfTheAboveClick}
                                    >
                                        {T._('None of these? Tell us in your own words', 'Admin')}
                                    </CustomOptionRow>
                                </OptionList>

                                {isCustom && (
                                    <PromptArea
                                        ref={promptRef}
                                        rows={4}
                                        maxLength={2000}
                                        disabled={isLoading}
                                        value={prompt}
                                        placeholder={PLACEHOLDER}
                                        onChange={(event) => setPrompt(event.target.value)}
                                    />
                                )}

                                <Actions>
                                    <PrimaryButton type='submit' $size='large' disabled={!prompt.trim() || isLoading}>
                                        {isLoading
                                            ? T._('Looking for the right solution...', 'Admin')
                                            : prompt.trim()
                                              ? T._('Continue', 'Admin')
                                              : T._('Pick a goal to continue', 'Admin')}
                                    </PrimaryButton>
                                    <ExploreLink onClick={goToSolutions}>{T._("I'd rather explore on my own", 'Admin')}</ExploreLink>
                                </Actions>
                            </Stack>
                        </form>
                    )}

                    {step === 'suggestion' && suggestion && (
                        <Stack $gap='24px'>
                            <ToolCard>
                                <CardLabel>{T._('Our suggestion', 'Admin')}</CardLabel>
                                <ToolName>{suggestion.tool.name}</ToolName>
                                <ToolDescription>{suggestion.tool.description}</ToolDescription>
                                <PrimaryButton type='button' $size='large' onClick={() => goToTool(suggestion.tool)}>
                                    {suggestion.tool.ctaLabel}
                                </PrimaryButton>
                            </ToolCard>

                            <Stack $gap='8px'>
                                <Subtitle>{T._('Not what you had in mind?', 'Admin')}</Subtitle>
                                <AlternativeRow>
                                    {suggestion.alternatives.map((tool) => (
                                        <BasicButton key={tool.id} type='button' onClick={() => goToTool(tool)}>
                                            {tool.name}
                                        </BasicButton>
                                    ))}
                                </AlternativeRow>
                            </Stack>

                            <Actions>
                                <TextButton onClick={() => setStep('prompt')}>{T._('Describe it again', 'Admin')}</TextButton>
                                <TextButton onClick={goToSolutions}>{T._('See all the solutions', 'Admin')}</TextButton>
                            </Actions>
                        </Stack>
                    )}
                </Content>
            )}

            {step === 'prompt' && !isOpenedFromApp && (
                <ShortcutFooter>
                    <span>{T._('Or go straight to:', 'Admin')}</span>
                    {ONBOARDING_TOOLS.map((tool) => (
                        <Shortcut key={tool.id} type='button' onClick={() => goToTool(tool)}>
                            {tool.name}
                        </Shortcut>
                    ))}
                </ShortcutFooter>
            )}
        </Container>
    );
};

export default Onboarding;
